import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderApi, truckContactApi, guestQuoteApi, orderLoadApi, sourcingChannelApi, employeeApi } from '../config/employeeApi';
import EditableCell from '../components/EditableCell';
import CompanyEditableCell from '../components/CompanyEditableCell';
import ConfirmOrderModal from '../components/ConfirmOrderModal';
import DocumentGenerator from '../components/DocumentGenerator';
import InvoiceGenerator from '../components/InvoiceGenerator';
import QuoteGenerator from '../components/QuoteGenerator';
import DriverQuoteGenerator from '../components/DriverQuoteGenerator';
import QBOSettings from '../components/QBOSettings';
import CargoItemsList from '../components/CargoItemsList';
import AIFileDropZone from '../components/AIFileDropZone';
import { Send, Map as MapIcon, Trash2 } from 'lucide-react';
import { loadGoogleMapsScript, diagnoseGoogleMapsIssues } from '../config/googleMaps';
import './BrokerOrdersNew.css';

const BrokerOrdersNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [stats, setStats] = useState({
    waiting_driver: 0,
    driver_found: 0,
    in_transit: 0
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [salesList, setSalesList] = useState([]);
  
  // 文档生成器状态
  const [showDocGenerator, setShowDocGenerator] = useState(false);
  const [documentType, setDocumentType] = useState(null); // 'BOL' 或 'RC'
  
  // 报价生成器状态
  const [showQuoteGenerator, setShowQuoteGenerator] = useState(false);

  // 司机报价生成器状态
  const [showDriverQuoteGenerator, setShowDriverQuoteGenerator] = useState(false);
  
  // Invoice 生成器状态
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  
  // QBO 设置状态
  const [showQBOSettings, setShowQBOSettings] = useState(false);
  
  // 文档管理状态
  const [orderDocs, setOrderDocs] = useState({}); // { orderId: { byType: {...} } }
  const [docUploading, setDocUploading] = useState({});

  // 无限滚动分页
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;
  
  const currentStatus = searchParams.get('status') || 'quote';
  
  const [guestSessions, setGuestSessions] = useState([]);
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestImporting, setGuestImporting] = useState({});
  const [expandedGuestRow, setExpandedGuestRow] = useState(null);

  // Order loads + workflow
  const [orderLoads, setOrderLoads] = useState({});
  const [loadsLoading, setLoadsLoading] = useState({});
  const [sourcingChannels, setSourcingChannels] = useState([]);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    employee: searchParams.get('employee') || 'all'
  });

  // Handle QBO callback redirect
  useEffect(() => {
    const qboStatus = searchParams.get('qbo');
    if (qboStatus === 'connected') {
      const company = searchParams.get('company');
      alert(`QuickBooks 已连接${company ? ': ' + company : ''}`);
      const params = new URLSearchParams(searchParams);
      params.delete('qbo'); params.delete('company'); params.delete('msg');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } else if (qboStatus === 'error') {
      alert('QuickBooks 连接失败: ' + (searchParams.get('msg') || '未知错误'));
      const params = new URLSearchParams(searchParams);
      params.delete('qbo'); params.delete('msg');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }
  }, [searchParams]);

  // 加载Google Maps API
  useEffect(() => {
    const initGoogleMaps = async () => {
      try {
        console.log('🗺️ 开始加载Google Maps API...');
        await loadGoogleMapsScript();
        console.log('✅ Google Maps API加载成功');
      } catch (error) {
        console.error('❌ Google Maps API加载失败:', error);
        console.log('🔍 运行诊断...');
        diagnoseGoogleMapsIssues();
      }
    };

    initGoogleMaps();
    loadGuestSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentStatus === 'guest_quotes') {
      loadGuestSessions();
    } else {
      // 切 tab / 改过滤条件 → 从第 1 页重新加载
      setPage(1);
      setHasMore(true);
      loadOrders(1, false);
      if (currentStatus === 'ordered') {
        loadStats();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatus, filters]);

  const loadOrders = async (pageNum = 1, append = false) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      // 重新从第 1 页加载（非 append）时，同步把分页状态归位
      if (!append) {
        setPage(1);
        setHasMore(true);
      }
      const response = await orderApi.getOrders({
        status: currentStatus,
        freight_mode: 'LTL',
        ...filters,
        page: pageNum,
        limit: PAGE_SIZE
      });
      
      if (response.success) {
        const incoming = response.data || [];
        setOrders(prev => append ? [...prev, ...incoming] : incoming);
        // 根据 pagination 判断还有没有下一页
        const pg = response.pagination;
        if (pg) {
          setHasMore(pg.page < pg.totalPages);
        } else {
          setHasMore(incoming.length === PAGE_SIZE);
        }
        // 报价单 / 已下单 / 已完成 都需要文档列，列表加载时预取
        if (currentStatus === 'quote' || currentStatus === 'ordered' || currentStatus === 'completed') {
          incoming.forEach(o => loadDocs(o.id));
        }
      }
    } catch (error) {
      console.error('加载订单失败:', error);
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  };

  // 滚动到接近底部时自动加载下一页
  const handleTableScroll = (e) => {
    if (loadingMore || !hasMore) return;
    const el = e.currentTarget;
    // 距离底部 < 120px 时触发
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      const next = page + 1;
      setPage(next);
      loadOrders(next, true);
    }
  };

  const loadGuestSessions = async () => {
    try {
      setGuestLoading(true);
      const response = await guestQuoteApi.getGuestSessions();
      if (response.success) {
        setGuestSessions(response.sessions || []);
      }
    } catch (error) {
      console.error('加载客人报价失败:', error);
    } finally {
      setGuestLoading(false);
    }
  };

  const loadOrderLoads = async (orderId) => {
    if (loadsLoading[orderId]) return;
    setLoadsLoading(prev => ({ ...prev, [orderId]: true }));
    try {
      const res = await orderLoadApi.getLoads(orderId);
      if (res.success) setOrderLoads(prev => ({ ...prev, [orderId]: res.data || [] }));
    } catch (e) { console.error('Load loads failed:', e); }
    finally { setLoadsLoading(prev => ({ ...prev, [orderId]: false })); }
  };

  useEffect(() => {
    sourcingChannelApi.getAll().then(res => {
      if (res.success) setSourcingChannels(res.data || []);
    }).catch(() => {});
  }, []);

  // 加载可分配的员工列表（用于"分配 Sales"下拉）
  useEffect(() => {
    employeeApi.getAssignableEmployees().then(res => {
      if (res.success) setSalesList(res.data || []);
    }).catch(() => {});
  }, []);

  const handleImportGuest = async (sessionId) => {
    if (!window.confirm('确定将此客人报价导入报价单？')) return;
    try {
      setGuestImporting(prev => ({ ...prev, [sessionId]: true }));
      const response = await guestQuoteApi.importSession(sessionId);
      if (response.success) {
        alert(`导入成功！WE单号: ${response.data.orderNumber}`);
        setGuestSessions(prev => prev.filter(s => s.session_id !== sessionId));
        if (currentStatus === 'quote') loadOrders();
      } else {
        alert(response.message || '导入失败');
      }
    } catch (error) {
      alert('导入失败: ' + error.message);
    } finally {
      setGuestImporting(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const loadStats = async () => {
    try {
      const response = await orderApi.getStatistics({ status: 'ordered', freight_mode: 'LTL' });
      if (response.success) {
        setStats({
          waiting_driver: response.data.waitingDriverCount || 0,
          driver_found: response.data.driverFoundCount || 0,
          in_transit: response.data.inTransitCount || 0
        });
      }
    } catch (error) {
      console.error('加载统计失败:', error);
    }
  };

  const handleStatusChange = (status) => {
    navigate(`/employee/broker-orders?status=${status}`);
    setExpandedRow(null);
  };

  // 获取纽约时间的 YYYY-MM-DD 格式日期
  const getNYDate = () => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(now); // 返回 YYYY-MM-DD 格式
  };

  // 创建空白订单
  const handleCreateOrder = async () => {
    try {
      setLoading(true);
      
      // 创建最小必填字段的订单（默认 LTL 散板模式）
      // 不预填公司名，留空让用户填写（CompanyEditableCell 会显示占位提示）
      const response = await orderApi.createOrder({
        customer_name: '',
        inquiry_company: '',
        cargo_description: '待填写',
        cargo_description_detailed: '待填写',
        order_type: 'land_freight',
        freight_mode: 'LTL',
        status: 'quote',
        quote_date: getNYDate()
      });
      
      if (response.success) {
        console.log('✅ 空白订单创建成功:', response.data);
        // 刷新列表
        await loadOrders();
        // 自动展开新订单
        setExpandedRow(response.data.id);
        // 提示用户
        alert('✅ 已创建空白订单，请双击各字段填写信息');
      }
    } catch (error) {
      console.error('❌ 创建订单失败:', error);
      const serverDetail = error.response?.detail
        || error.response?.error
        || error.response?.message
        || '';
      const serverCode = error.response?.code ? ` [${error.response.code}]` : '';
      alert(`创建失败: ${error.message}${serverCode}${serverDetail && serverDetail !== error.message ? '\n\n详细: ' + serverDetail : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOrder = async (orderId) => {
    // 找到对应的订单
    const order = orders.find(o => o.id === orderId);
    if (!order) {
      alert('订单不存在');
      return;
    }
    
    // 显示确认下单模态框
    setSelectedOrder(order);
    setShowConfirmModal(true);
  };

  const handleConfirmOrderSubmit = async (formData) => {
    try {
      // 先确认订单
      const response = await orderApi.confirmOrder(selectedOrder.id, 'waiting_driver');
      
      if (response.success) {
        const truckPayment = parseFloat(formData.truck_payment) || 0;
        const wePrice = parseFloat(selectedOrder.ew_quote_price) || 0;
        const customerExtra = parseFloat(selectedOrder.customer_extra_fee) || 0;
        const driverExtra = parseFloat(selectedOrder.driver_extra_fee) || 0;
        // profit = 客户侧总收入 − 司机侧总支付
        const profit = (wePrice + customerExtra) - (truckPayment + driverExtra);

        await orderApi.updateOrder(selectedOrder.id, {
          quote_date: getNYDate(),
          // 分配的 Sales（被分配者成为该订单的操作员工）
          ...(formData.assigned_sales_id ? { assigned_to: parseInt(formData.assigned_sales_id) } : {}),
          truck_payment: truckPayment,
          truck_reference_price: formData.truck_reference_price ? parseFloat(formData.truck_reference_price) : null,
          profit: profit,
          mc_number: formData.mc_number,
          dot_number: formData.dot_number || null,
          truck_company_name: formData.truck_company_name,
          truck_contact: formData.truck_contact,
          carrier_email: formData.carrier_email || null,
          carrier_address: formData.carrier_address || null,
          driver_name: formData.driver_name || null,
          driver_phone: formData.driver_phone || null,
          backup_driver_1_name: formData.backup_driver_1_name || null,
          backup_driver_1_phone: formData.backup_driver_1_phone || null,
          backup_driver_2_name: formData.backup_driver_2_name || null,
          backup_driver_2_phone: formData.backup_driver_2_phone || null,
          backup_driver_3_name: formData.backup_driver_3_name || null,
          backup_driver_3_phone: formData.backup_driver_3_phone || null
        });
        
        try {
          await truckContactApi.upsertContact({
            mc_number: formData.mc_number,
            truck_company_name: formData.truck_company_name,
            truck_contact: formData.truck_contact,
            dot_number: formData.dot_number || null,
            carrier_email: formData.carrier_email || null
          });
        } catch (e) {
          console.warn('Auto-save contact failed:', e);
        }

        alert('订单已确认下单，卡车和备用司机信息已保存！');
        
        // 关闭模态框
        setShowConfirmModal(false);
        setSelectedOrder(null);
        
        // 刷新列表
        loadOrders();
        
        // 切换到已下单标签
        navigate('/employee/broker-orders?status=ordered');
      }
    } catch (error) {
      console.error('下单失败:', error);
      alert('操作失败: ' + error.message);
    }
  };

  const toggleRow = (orderId) => {
    const newExpanded = expandedRow === orderId ? null : orderId;
    setExpandedRow(newExpanded);
    // 展开报价单 / 已下单 / 已完成订单时自动加载文档
    if (newExpanded && (currentStatus === 'quote' || currentStatus === 'ordered' || currentStatus === 'completed')) {
      loadDocs(orderId);
    }
  };

  // 计算报价参考及相关字段
  const calculateQuoteReferences = (order) => {
    let totalDat = parseFloat(order.total_dat) || 0;
    const truckPallets = parseFloat(order.truck_pallets) || 1; // 避免除以0
    const totalAreaPallets = parseFloat(order.total_area_pallets) || 0;
    const addressType = order.address_type || '';
    
    // 如果是 Residential，TOTAL DAT 乘以 0.7
    if (addressType === 'Residential') {
      totalDat = totalDat * 0.7;
    }
    
    // 报价参考 = (TOTAL DAT / 价格系数) × 总面积板数 + 100
    const quoteReference = (totalDat / truckPallets) * totalAreaPallets + 100;
    
    // 参考+10%, +20%, +30%
    const quoteRef10 = quoteReference * 1.1;
    const quoteRef20 = quoteReference * 1.2;
    const quoteRef30 = quoteReference * 1.3;
    
    return {
      quote_reference: quoteReference,
      quote_ref_10: quoteRef10,
      quote_ref_20: quoteRef20,
      quote_ref_30: quoteRef30
    };
  };

  // 单元格更新处理
  const handleCellUpdate = async (orderId, field, newValue) => {
    try {
      console.log('💾 更新字段:', { orderId, field, newValue });
      
      const response = await orderApi.updateOrder(orderId, {
        [field]: newValue
      });
      
      if (response.success) {
        // 用后端返回的完整订单去同步本地 state —— 这样像 profit 这类后端
        // 自动重算的字段（ew_quote_price / truck_payment /
        // customer_extra_fee / driver_extra_fee 变动时）会自动反映出来。
        const updated = response.data || { [field]: newValue };
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, ...updated }
              : order
          )
        );
        console.log('✅ 更新成功');
      }
    } catch (error) {
      console.error('❌ 更新失败:', error);
      throw error;
    }
  };

  // 当前登录员工标识 + 角色
  const currentEmail = (user?.attributes?.email || user?.email || user?.username || '').toLowerCase();
  const isAdmin = user?.employeeRole === 'admin';

  // 是否有权修改某订单：
  //  - 报价单阶段：所有员工开放
  //  - 已下单及之后：仅被分配的 Sales 或 admin（未分配时回退创建人）
  const canModifyOrder = (order) => {
    if (!order) return false;
    if (order.status === 'quote') return true;
    if (isAdmin) return true;
    const assigneeEmail = (order.assignee_info?.email || '').toLowerCase();
    if (assigneeEmail) return assigneeEmail === currentEmail;
    const creatorEmail = (order.creator_info?.email || '').toLowerCase();
    return !!creatorEmail && creatorEmail === currentEmail;
  };

  // Admin 在"操作员工"列重新分配 Sales
  const handleReassign = async (orderId, newAssigneeId) => {
    try {
      await orderApi.updateOrder(orderId, { assigned_to: newAssigneeId ? parseInt(newAssigneeId) : null });
      await loadOrders();
    } catch (error) {
      alert('重新分配失败: ' + error.message);
    }
  };

  // Company 列保存：写入公司名的同时把"待确认(AI识别)"标记清除（转为已确认/黑色）
  const handleCompanySave = async (orderId, field, newValue) => {
    try {
      const response = await orderApi.updateOrder(orderId, {
        inquiry_company: newValue,
        inquiry_company_confirmed: true
      });
      const updated = response.success ? (response.data || {}) : {};
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId
            ? { ...order, inquiry_company: newValue, inquiry_company_confirmed: true, customer_name: newValue || order.customer_name, ...updated }
            : order
        )
      );
    } catch (error) {
      console.error('❌ 保存公司名失败:', error);
      throw error;
    }
  };

  // 单元格更新处理（带自动计算）
  const handleCellUpdateWithCalculation = async (orderId, field, newValue) => {
    try {
      console.log('💾 更新字段并计算:', { orderId, field, newValue });
      
      // 获取当前订单数据
      const currentOrder = orders.find(o => o.id === orderId);
      if (!currentOrder) return;
      
      // 创建更新后的订单对象
      const updatedOrder = { ...currentOrder, [field]: newValue };
      
      // 计算报价参考及相关字段
      const calculatedFields = calculateQuoteReferences(updatedOrder);
      
      // 合并所有要更新的字段
      const updateData = {
        [field]: newValue,
        ...calculatedFields
      };
      
      const response = await orderApi.updateOrder(orderId, updateData);
      
      if (response.success) {
        // 更新本地state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, ...updateData }
              : order
          )
        );
        console.log('✅ 更新成功并自动计算完成');
      }
    } catch (error) {
      console.error('❌ 更新失败:', error);
      throw error;
    }
  };

  // Google Maps 地址处理和距离计算
  const calculateAddressDetails = async (orderId, type, originAddr, destAddr) => {
    try {
      if (!window.google || !window.google.maps) {
        console.warn('⚠️ Google Maps API未加载，跳过自动计算');
        return;
      }

      console.log('🗺️ 开始计算地址详情...', { type, originAddr, destAddr });

      // 1. Geocoding提取城市、州、邮编
      const geocoder = new window.google.maps.Geocoder();
      
      const geocodePromise = (address) => {
        return new Promise((resolve, reject) => {
          // 清理地址（移除多余空格和换行，智能格式化）
          let cleanAddress = address.trim()
            .replace(/\n+/g, ' ')      // 换行变空格
            .replace(/\s+/g, ' ')      // 多个空格变单个
            .replace(/\s*,\s*/g, ', '); // 统一逗号格式
          
          console.log('🗺️ 清理后的地址:', cleanAddress);
          
          geocoder.geocode({ address: cleanAddress }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const components = results[0].address_components;
              let city = '', state = '', zipcode = '';
              
              components.forEach(component => {
                // 城市可能在locality或sublocality
                if (component.types.includes('locality')) {
                  city = component.long_name;
                } else if (!city && component.types.includes('sublocality')) {
                  city = component.long_name;
                }
                
                // 州
                if (component.types.includes('administrative_area_level_1')) {
                  state = component.short_name; // CA, NY, ID等
                }
                
                // 邮编
                if (component.types.includes('postal_code')) {
                  zipcode = component.long_name;
                }
              });
              
              console.log('🗺️ 地址解析成功:', { city, state, zipcode, address: cleanAddress });
              resolve({ city, state, zipcode });
            } else {
              console.warn('⚠️ 地址解析失败:', status);
              reject(new Error('地址解析失败: ' + status));
            }
          });
        });
      };

      // 2. 使用zipcode计算距离（更可靠）
      const distanceService = new window.google.maps.DistanceMatrixService();
      
      const calculateDistByZipcode = (originZip, destZip) => {
        return new Promise((resolve, reject) => {
          if (!originZip || !destZip) {
            reject(new Error('缺少邮编'));
            return;
          }
          
          console.log('🗺️ 使用邮编计算距离:', { originZip, destZip });
          
          distanceService.getDistanceMatrix({
            origins: [originZip],
            destinations: [destZip],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.IMPERIAL
          }, (response, status) => {
            console.log('🗺️ 距离API响应:', { status, response });
            
            if (status === 'OK') {
              const element = response.rows[0].elements[0];
              if (element.status === 'OK') {
                const distanceValue = element.distance.value; // 米
                const distanceMiles = Math.round(distanceValue * 0.000621371); // 转换为英里
                console.log('✅ 距离计算成功:', distanceMiles, 'miles');
                resolve(distanceMiles);
              } else {
                console.warn('⚠️ 距离计算元素错误:', element.status);
                reject(new Error('无法计算距离: ' + element.status));
              }
            } else {
              console.warn('⚠️ 距离计算失败:', status);
              reject(new Error('距离计算失败: ' + status));
            }
          });
        });
      };

      // 3. 先解析地址获取zipcode
      let originInfo = null;
      let destInfo = null;
      let originZip = null;
      let destZip = null;
      
      // 解析当前更新的地址
      if (type === 'origin') {
        originInfo = await geocodePromise(originAddr);
        originZip = originInfo.zipcode;
      }
      
      if (type === 'destination') {
        destInfo = await geocodePromise(destAddr);
        destZip = destInfo.zipcode;
      }
      
      // 4. 获取另一个地址的zipcode（从数据库或刚解析的）
      if (!originZip && type === 'destination') {
        // 从订单数据获取发货zipcode
        const currentOrder = orders.find(o => o.id === orderId);
        originZip = currentOrder?.origin_zipcode;
      }
      
      if (!destZip && type === 'origin') {
        // 从订单数据获取收货zipcode
        const currentOrder = orders.find(o => o.id === orderId);
        destZip = currentOrder?.destination_zipcode;
      }
      
      // 5. 使用zipcode计算距离
      let distance = null;
      if (originZip && destZip) {
        try {
          console.log('🗺️ 准备用邮编计算距离:', { originZip, destZip });
          distance = await calculateDistByZipcode(originZip, destZip);
        } catch (err) {
          console.warn('⚠️ 距离计算失败:', err);
        }
      } else {
        console.warn('⚠️ 缺少邮编，无法计算距离:', { originZip, destZip });
      }

      // 6. 更新所有相关字段
      const updates = {};
      if (originInfo) {
        updates.origin_city = originInfo.city;
        updates.origin_state = originInfo.state;
        updates.origin_zipcode = originInfo.zipcode;
      }
      if (destInfo) {
        updates.destination_city = destInfo.city;
        updates.destination_state = destInfo.state;
        updates.destination_zipcode = destInfo.zipcode;
      }
      if (distance !== null) {
        updates.transport_distance = distance;
      }

      console.log('🗺️ 计算结果:', updates);

      if (Object.keys(updates).length > 0) {
        const response = await orderApi.updateOrder(orderId, updates);
        
        if (response.success) {
          console.log('✅ 地址详情已自动更新，刷新列表...');
          
          // 重新加载整个订单列表以确保UI同步
          await loadOrders();
          
          // 显示成功提示
          const updateMsg = [];
          if (updates.origin_city || updates.destination_city) {
            updateMsg.push('城市/州已提取');
          }
          if (updates.transport_distance) {
            updateMsg.push(`距离: ${updates.transport_distance} mi`);
          }
          
          if (updateMsg.length > 0) {
            alert('✅ ' + updateMsg.join(', '));
          }
        }
      }
    } catch (error) {
      console.error('❌ 地址计算失败:', error);
      alert('⚠️ 地址自动计算失败，请检查地址格式或手动输入');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '-';
    return parseFloat(num).toLocaleString();
  };

  const formatCurrency = (num) => {
    if (!num) return '-';
    return `$${parseFloat(num).toLocaleString()}`;
  };

  const getSubStatusBadge = (subStatus) => {
    if (!subStatus) return null;
    const labels = {
      waiting_driver: '寻找司机',
      driver_found: '找到司机',
      sent_to_3pl: '给3PL',
      in_transit: '运输中'
    };
    return (
      <span className={`status-badge sub-status-${subStatus}`}>
        {labels[subStatus]}
      </span>
    );
  };

  const handleUpdateSubStatus = async (orderId, subStatus) => {
    try {
      const response = await orderApi.updateSubStatus(orderId, subStatus);
      if (response.success) {
        console.log('✅ 子状态更新成功:', subStatus);
        await loadOrders();
      }
    } catch (error) {
      console.error('❌ 更新子状态失败:', error);
      alert('更新状态失败: ' + error.message);
    }
  };

  const handleCompleteOrder = async (orderId) => {
    if (!window.confirm('确定要将此订单标记为"已完成"吗？')) {
      return;
    }
    
    try {
      const response = await orderApi.completeOrder(orderId);
      if (response.success) {
        console.log('✅ 订单已完成');
        await loadOrders();
      }
    } catch (error) {
      console.error('❌ 完成订单失败:', error);
      alert('完成订单失败: ' + error.message);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('确定要取消此订单吗？取消后订单将移到"已取消"标签。')) {
      return;
    }
    
    try {
      const response = await orderApi.cancelOrder(orderId);
      if (response.success) {
        console.log('✅ 订单已取消');
        await loadOrders();
        // 如果需要，切换到已取消标签
        navigate('/employee/broker-orders?status=cancelled');
      }
    } catch (error) {
      console.error('❌ 取消订单失败:', error);
      alert('取消订单失败: ' + error.message);
    }
  };

  const handleRequestClaim = async (orderId) => {
    const claimReason = prompt('请输入索赔原因:');
    if (!claimReason || claimReason.trim() === '') {
      alert('索赔原因不能为空！');
      return;
    }
    
    try {
      const response = await orderApi.requestClaim(orderId, claimReason);
      if (response.success) {
        alert('索赔申请已提交！');
        await loadOrders();
        // 切换到需要索赔标签
        navigate('/employee/broker-orders?status=claim');
      }
    } catch (error) {
      console.error('❌ 申请索赔失败:', error);
      alert('申请索赔失败: ' + error.message);
    }
  };

  const handleReopenOrder = async (orderId) => {
    if (!window.confirm('确定要重新打开此订单吗？订单将回到"已下单"状态。')) {
      return;
    }
    
    try {
      const response = await orderApi.updateOrder(orderId, {
        status: 'ordered',
        sub_status: 'waiting_driver'
      });
      if (response.success) {
        alert('订单已重新打开！');
        await loadOrders();
        // 切换到已下单标签
        navigate('/employee/broker-orders?status=ordered');
      }
    } catch (error) {
      console.error('❌ 重新打开订单失败:', error);
      alert('重新打开订单失败: ' + error.message);
    }
  };

  // ========== 文档管理 ==========
  const DOC_TYPES = [
    { key: 'quote', label: '客户报价' },
    { key: 'driver_quote', label: '司机报价' },
    { key: 'bol', label: 'BOL' },
    { key: 'rc', label: 'RC' },
    { key: 'pod', label: 'POD' },
    { key: 'customer_invoice', label: '发票' },
    { key: 'vendor_invoice', label: '司机发票' },
    { key: 'driver_id', label: '司机ID' },
    { key: 'vin_pic', label: 'VIN Pic' },
    { key: 'coi', label: 'COI' },
    { key: 'w9', label: 'W-9' },
  ];

  const loadDocs = async (orderId) => {
    try {
      const res = await orderApi.getDocuments(orderId);
      if (res.success) setOrderDocs(prev => ({ ...prev, [orderId]: res.data.byType }));
    } catch (e) { console.error('加载文档失败:', e); }
  };

  const handleDocUpload = async (orderId, file, docType) => {
    const uploadKey = `${orderId}-${docType}`;
    setDocUploading(prev => ({ ...prev, [uploadKey]: true }));
    try {
      await orderApi.uploadDocument(orderId, file, docType);
      await loadDocs(orderId);
    } catch (e) { alert('上传失败: ' + e.message); }
    finally { setDocUploading(prev => ({ ...prev, [uploadKey]: false })); }
  };

  const handleDocDownload = async (orderId, docId, filename) => {
    try { await orderApi.downloadDocument(orderId, docId, filename); }
    catch (e) { alert('下载失败: ' + e.message); }
  };

  const handleDocDelete = async (orderId, docId, docType) => {
    if (!window.confirm('确定删除此文档？')) return;
    try { await orderApi.deleteDocument(orderId, docId); await loadDocs(orderId); }
    catch (e) { alert('删除失败: ' + e.message); }
  };

  // 文档 cell —— quote / ordered / completed 三个 tab 共用
  const renderDocsCell = (order) => (
    <td className="td-docs" onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', gap: '3px 6px', flexWrap: 'wrap', fontSize: 10.5, lineHeight: 1.5 }}>
        {DOC_TYPES.map(dt => {
          const doc = orderDocs[order.id]?.[dt.key];
          const uploading = docUploading[`${order.id}-${dt.key}`];
          if (uploading) return <span key={dt.key} style={{ color: '#9ca3af' }}>...</span>;
          if (doc) {
            return (
              <span key={dt.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}>
                <span
                  style={{ color: '#1565C0', cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
                  onClick={() => handleDocDownload(order.id, doc.id, doc.original_filename)}
                >{dt.label}</span>
                {isAdmin && (
                  <span
                    style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 10 }}
                    onClick={() => handleDocDelete(order.id, doc.id, dt.key)}
                  >x</span>
                )}
              </span>
            );
          }
          return (
            <label key={dt.key} style={{ color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }} title="点击上传">
              {dt.label}
              <input type="file" style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.tiff,.tif"
                onChange={(e) => { if (e.target.files[0]) { handleDocUpload(order.id, e.target.files[0], dt.key); e.target.value = ''; } }}
              />
            </label>
          );
        })}
      </div>
    </td>
  );

  // 快速标记付款
  const handleMarkPaid = async (orderId, status) => {
    try {
      await orderApi.markPaid(orderId, { payment_status: status });
      await loadOrders();
    } catch (e) { alert('操作失败: ' + e.message); }
  };

  const handleBackToQuote = async (orderId) => {
    if (!window.confirm('确定要回到报价状态吗？这将清除所有卡车信息（付卡车价格、MC Number、卡车公司、联络方式），如需重新下单需重新填写。')) {
      return;
    }
    
    try {
      const response = await orderApi.updateOrder(orderId, {
        status: 'quote',
        sub_status: null,
        truck_payment: null,
        mc_number: null,
        truck_company_name: null,
        truck_contact: null,
        confirmed_by: null,
        confirmed_at: null
      });
      if (response.success) {
        alert('订单已回到报价状态，卡车信息已清除！');
        await loadOrders();
        // 切换到报价单标签
        navigate('/employee/broker-orders?status=quote');
      }
    } catch (error) {
      console.error('❌ 回到报价失败:', error);
      alert('回到报价失败: ' + error.message);
    }
  };

  return (
    <div className="broker-orders-container">
      {/* 侧边栏 */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h2>订单管理</h2>
        </div>

        {/* LTL / FTL mode toggle */}
        <div className="ltl-ftl-toggle" style={{
          display: 'flex',
          gap: 4,
          padding: '10px 12px',
          background: 'rgba(0,0,0,0.15)',
          borderBottom: '1px solid rgba(255,255,255,0.05)'
        }}>
          <button
            className="mode-btn active"
            title="当前页"
            style={{
              flex: 1,
              padding: '8px 6px',
              background: '#3498db',
              color: 'white',
              border: '1px solid #2c7be5',
              borderRadius: 6,
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            LTL 散板
          </button>
          <button
            className="mode-btn"
            onClick={() => navigate('/employee/ftl-orders')}
            title="切换到 FTL 整车订单"
            style={{
              flex: 1,
              padding: '8px 6px',
              background: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.7)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6,
              fontSize: '0.8rem',
              cursor: 'pointer',
            }}
          >
            FTL 整车
          </button>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${currentStatus === 'guest_quotes' ? 'active' : ''}`}
            onClick={() => handleStatusChange('guest_quotes')}
            style={currentStatus === 'guest_quotes' ? {} : { color: '#e67e22', fontWeight: 600 }}
          >
            🔔 客人Quote {guestSessions.length > 0 && `(${guestSessions.length})`}
          </button>

          <button
            className={`nav-item ${currentStatus === 'quote' ? 'active' : ''}`}
            onClick={() => handleStatusChange('quote')}
          >
            报价单
          </button>
          
          <button
            className={`nav-item ${currentStatus === 'ordered' ? 'active' : ''}`}
            onClick={() => handleStatusChange('ordered')}
          >
            已下单
          </button>
          
          <button
            className={`nav-item ${currentStatus === 'completed' ? 'active' : ''}`}
            onClick={() => handleStatusChange('completed')}
          >
            已完成
          </button>
          
          <button
            className={`nav-item ${currentStatus === 'cancelled' ? 'active' : ''}`}
            onClick={() => handleStatusChange('cancelled')}
          >
            已取消
          </button>
          
          <button
            className={`nav-item ${currentStatus === 'claim' ? 'active' : ''}`}
            onClick={() => handleStatusChange('claim')}
          >
            需索赔
          </button>
        </nav>

        {/* 分割线 */}
        <div className="nav-divider"></div>

        {/* Customers */}
        <button
          className="nav-item"
          onClick={() => navigate('/employee/customers')}
        >
          Customers
        </button>

        {/* Vendors */}
        <button
          className="nav-item"
          onClick={() => navigate('/employee/vendors')}
        >
          Vendors
        </button>

        {/* 付款管理 */}
        <button
          className="nav-item"
          onClick={() => navigate('/employee/payments')}
        >
          付款管理
        </button>

        {/* 地图查单 */}
        <button
          className="nav-item"
          onClick={() => navigate('/employee/map-view')}
        >
          地图查单
        </button>

        {/* AI 报价审核 */}
        <button
          className="nav-item"
          onClick={() => navigate('/employee/ai-review')}
        >
          AI 报价审核
        </button>

        {/* DAT Load Board */}
        <button
          className="nav-item"
          onClick={() => navigate('/employee/dat-loadboard')}
        >
          DAT Load Board
        </button>

        {/* 司机联系簿 */}
        <button
          className="nav-item"
          onClick={() => navigate('/employee/driver-contacts')}
        >
          司机联系簿
        </button>

        {/* Shipments */}
        <button
          className="nav-item"
          onClick={() => {
            const count = prompt('Shipments management is available via API.\n\nPOST /api/shipments to create\nGET /api/shipments to list\n\nFull UI coming in next update.');
          }}
          style={{ fontSize: '0.85rem' }}
        >
          Shipments
        </button>

        {/* 广告管理 */}
        <button
          className="nav-item"
          onClick={() => navigate('/employee/ads')}
        >
          广告管理
        </button>

        {/* QuickBooks 设置 */}
        <button
          className="nav-item"
          onClick={() => setShowQBOSettings(true)}
        >
          QuickBooks
        </button>

        {/* 检查逾期 */}
        <button
          className="nav-item"
          onClick={async () => {
            try {
              const res = await orderApi.checkOverdue();
              if (res.success && res.data.length > 0) {
                const list = res.data.map(o => `${o.ew_quote_number || o.order_number}: ${o.inquiry_company} - 逾期${o.days_overdue}天, 欠款$${o.outstanding}, Late Fee$${o.late_fee}`).join('\n');
                alert(`发现 ${res.data.length} 个逾期订单:\n\n${list}`);
              } else {
                alert('没有逾期订单');
              }
            } catch (e) { alert('检查失败: ' + e.message); }
          }}
        >
          检查逾期
        </button>

        {/* 已下单的子状态统计 */}
        {currentStatus === 'ordered' && (
          <div className="sub-status-stats">
            <h3>订单状态</h3>
            <div className="stat-item waiting-driver">
              <span>寻找司机</span>
              <strong>{stats.waiting_driver}</strong>
            </div>
            <div className="stat-item driver-found">
              <span>找到司机</span>
              <strong>{stats.driver_found}</strong>
            </div>
            <div className="stat-item in-transit">
              <span>运输中</span>
              <strong>{stats.in_transit}</strong>
            </div>
          </div>
        )}

        {/* 系统管理 */}
        {['admin', 'accountant'].includes(user?.employeeRole) && (
          <div className="sidebar-footer">
            <button
              className="nav-item nav-admin"
              onClick={() => navigate('/employee/credits-admin')}
            >
              💰 Credits Management
            </button>
            {user?.employeeRole === 'admin' && (
              <button
                className="nav-item nav-admin"
                onClick={() => navigate('/employee/admin')}
              >
                ⚙️ 系统管理
              </button>
            )}
          </div>
        )}
      </div>

      {/* 主内容区 */}
      <div className="main-content">
        {currentStatus === 'guest_quotes' ? (
          <div className="guest-quotes-view">
            <div className="toolbar">
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>客人报价（未进入报价单）</h3>
              <button className="btn-create" onClick={loadGuestSessions} disabled={guestLoading}>
                {guestLoading ? '加载中...' : '🔄 刷新'}
              </button>
            </div>
            {guestLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>加载中...</div>
            ) : guestSessions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>暂无客人报价</div>
            ) : (
              <div className="table-wrapper">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th>日期</th>
                      <th>客人信息</th>
                      <th>发货地</th>
                      <th>收货地</th>
                      <th>重量(lbs)</th>
                      <th>托盘数</th>
                      <th>运输商数</th>
                      <th>最低报价</th>
                      <th>状态</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guestSessions.map(s => {
                      const isExpired = new Date(s.expires_at) < new Date();
                      const userName = [s.user_first_name, s.user_last_name].filter(Boolean).join(' ');
                      const isExpanded = expandedGuestRow === s.session_id;
                      const items = Array.isArray(s.items) ? s.items : [];
                      const quotes = Array.isArray(s.quote_results) ? s.quote_results : [];
                      const pickupSvc = Array.isArray(s.pickup_services) ? s.pickup_services : [];
                      const deliverySvc = Array.isArray(s.delivery_services) ? s.delivery_services : [];
                      return (
                        <React.Fragment key={s.session_id}>
                        <tr style={isExpired ? { opacity: 0.5 } : { cursor: 'pointer' }} onClick={() => setExpandedGuestRow(isExpanded ? null : s.session_id)}>
                          <td>
                            <button className="expand-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 6px' }}>
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          </td>
                          <td>{new Date(s.created_at).toLocaleDateString('zh-CN')}</td>
                          <td style={{ maxWidth: 220 }}>
                            {userName && <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{userName}</div>}
                            {s.user_company && <div style={{ fontSize: '0.75rem', color: '#666' }}>{s.user_company}</div>}
                            <div style={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.user_email}</div>
                            {s.user_phone && <div style={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: 500 }}>📞 {s.user_phone}</div>}
                          </td>
                          <td>
                            <div style={{ fontWeight: 500 }}>{[s.origin_city, s.origin_state].filter(Boolean).join(', ')}</div>
                            {s.origin_zip && <div style={{ fontSize: '0.75rem', color: '#666' }}>ZIP: {s.origin_zip}</div>}
                          </td>
                          <td>
                            <div style={{ fontWeight: 500 }}>{[s.destination_city, s.destination_state].filter(Boolean).join(', ')}</div>
                            {s.destination_zip && <div style={{ fontSize: '0.75rem', color: '#666' }}>ZIP: {s.destination_zip}</div>}
                          </td>
                          <td>{s.total_weight ? Number(s.total_weight).toLocaleString() : '—'}</td>
                          <td>{s.total_pallets || '—'}</td>
                          <td>{s.quote_count || 0}</td>
                          <td style={{ fontWeight: 600, color: '#16a34a' }}>{s.lowest_price ? `$${Number(s.lowest_price).toFixed(2)}` : '—'}</td>
                          <td>{isExpired ? <span style={{ color: '#ef4444' }}>已过期</span> : <span style={{ color: '#16a34a' }}>有效</span>}</td>
                          <td>
                            <button
                              className="btn-create"
                              style={{ fontSize: '0.8rem', padding: '4px 12px', whiteSpace: 'nowrap' }}
                              onClick={(e) => { e.stopPropagation(); handleImportGuest(s.session_id); }}
                              disabled={guestImporting[s.session_id] || isExpired}
                            >
                              {guestImporting[s.session_id] ? '导入中...' : '加入报价单'}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="expanded-row">
                            <td colSpan="100%">
                              <div className="expanded-content">
                                {/* 路线与基本信息 */}
                                <div className="detail-grid" style={{ marginBottom: 16 }}>
                                  <div className="detail-item">
                                    <label>发货地:</label>
                                    <span>{[s.origin_city, s.origin_state, s.origin_zip].filter(Boolean).join(', ') || '—'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>收货地:</label>
                                    <span>{[s.destination_city, s.destination_state, s.destination_zip].filter(Boolean).join(', ') || '—'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>发货地址类型:</label>
                                    <span>{s.origin_location_type || '—'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>收货地址类型:</label>
                                    <span>{s.destination_location_type || '—'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>距离:</label>
                                    <span>{s.distance_miles ? `${Number(s.distance_miles).toLocaleString()} miles` : '—'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>取货日期:</label>
                                    <span>{s.pickup_date ? new Date(s.pickup_date).toLocaleDateString('zh-CN') : '—'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>送货日期:</label>
                                    <span>{s.delivery_date ? new Date(s.delivery_date).toLocaleDateString('zh-CN') : '—'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>报价过期:</label>
                                    <span>{s.expires_at ? new Date(s.expires_at).toLocaleString('zh-CN') : '—'}</span>
                                  </div>
                                </div>

                                {/* 附加服务 */}
                                {(pickupSvc.length > 0 || deliverySvc.length > 0) && (
                                  <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>附加服务</h4>
                                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                                      {pickupSvc.length > 0 && (
                                        <div><strong>取货:</strong> {pickupSvc.join(', ')}</div>
                                      )}
                                      {deliverySvc.length > 0 && (
                                        <div><strong>送货:</strong> {deliverySvc.join(', ')}</div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* 客人联系信息 */}
                                <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0f7ff', borderRadius: 8, border: '1px solid #dbeafe' }}>
                                  <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>客人联系信息</h4>
                                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: '0.9rem' }}>
                                    {userName && <div><strong>姓名:</strong> {userName}</div>}
                                    {s.user_company && <div><strong>公司:</strong> {s.user_company}</div>}
                                    <div><strong>邮箱:</strong> {s.user_email}</div>
                                    {s.user_phone && <div><strong>电话:</strong> <a href={`tel:${s.user_phone}`} style={{ color: '#2563eb' }}>{s.user_phone}</a></div>}
                                  </div>
                                </div>

                                {/* 货物明细 */}
                                {items.length > 0 && (
                                  <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>货物明细 ({items.length} 项)</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                      <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                          <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>#</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>描述</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>托盘</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>重量(lbs)</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>长(in)</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>宽(in)</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>高(in)</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Class</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>可叠</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>危险品</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {items.map((item, idx) => (
                                          <tr key={item.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '6px 10px' }}>{idx + 1}</td>
                                            <td style={{ padding: '6px 10px' }}>{item.description || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item.pallets || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 500 }}>{item.weight ? Number(item.weight).toLocaleString() : '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item.length || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item.width || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item.height || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item.freightClass || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item.stackable ? '✅' : '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item.hazmat ? '⚠️' : '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}

                                {/* 各承运商报价 */}
                                {quotes.length > 0 && (
                                  <div>
                                    <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>承运商报价 ({quotes.length} 家)</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                      <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                          <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>承运商</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>报价</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>时效(天)</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>服务</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>保障</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Quote ID</th>
                                          <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>有效期</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {quotes.sort((a, b) => (a.price || Infinity) - (b.price || Infinity)).map((q, idx) => (
                                          <tr key={q.id || idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx === 0 ? '#f0fdf4' : 'transparent' }}>
                                            <td style={{ padding: '6px 10px', fontWeight: 500 }}>{q.carrier || q.carrierCode || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: idx === 0 ? '#16a34a' : '#333' }}>${Number(q.price || 0).toFixed(2)}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{q.transitDays || '—'}</td>
                                            <td style={{ padding: '6px 10px', fontSize: '0.8rem' }}>{q.serviceType || q.serviceLevel || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{q.isGuaranteed ? '✅' : '—'}</td>
                                            <td style={{ padding: '6px 10px', fontSize: '0.75rem', color: '#666' }}>{q.quoteId || '—'}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'center', fontSize: '0.8rem' }}>{q.expDate ? new Date(q.expDate).toLocaleDateString('zh-CN') : '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
        <>
        {/* 工具栏 */}
        <div className="toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="搜索订单号、公司..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
          />
          
          <select
            className="filter-select"
            value={filters.employee}
            onChange={(e) => setFilters({...filters, employee: e.target.value})}
          >
            <option value="all">所有订单</option>
            <option value="mine">我的订单</option>
          </select>

          <button 
            className="btn-create" 
            onClick={handleCreateOrder}
            disabled={loading}
          >
            {loading ? '创建中...' : '+ 新建报价单'}
          </button>

          {/* 报价单：批量生成客户报价 */}
          {currentStatus === 'quote' && (
            <button
              className="btn-header btn-quote-header"
              onClick={(e) => {
                e.stopPropagation();
                setShowQuoteGenerator(true);
              }}
              title="批量生成客户报价"
            >
              📋 客户报价
            </button>
          )}

          {/* 已下单：批量生成司机报价 / BOL / RC 按钮 */}
          {currentStatus === 'ordered' && (
            <>
              <button
                className="btn-header btn-quote-header"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDriverQuoteGenerator(true);
                }}
                title="批量生成司机报价"
              >
                🚚 司机报价
              </button>
              <button
                className="btn-header btn-bol-header"
                onClick={(e) => {
                  e.stopPropagation();
                  setDocumentType('BOL');
                  setShowDocGenerator(true);
                }}
                title="批量生成BOL"
              >
                BOL
              </button>
              <button
                className="btn-header btn-rc-header"
                onClick={(e) => {
                  e.stopPropagation();
                  setDocumentType('RC');
                  setShowDocGenerator(true);
                }}
                title="批量生成RC"
              >
                RC
              </button>
            </>
          )}

          {/* 已完成：批量生成 Invoice */}
          {currentStatus === 'completed' && (
            <button
              className="btn-header btn-invoice-header"
              onClick={(e) => {
                e.stopPropagation();
                setShowInvoiceGenerator(true);
              }}
              title="批量生成Invoice"
            >
              📄 Invoice
            </button>
          )}
        </div>

        {/* AI 文件解析区域 - 仅在 quote tab 显示 */}
        {currentStatus === 'quote' && (
          <AIFileDropZone onOrdersCreated={() => loadOrders()} />
        )}

        {/* 订单表格 */}
        {loading ? (
          <div className="loading">加载中...</div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <p>暂无订单</p>
            <button className="btn-create" onClick={handleCreateOrder}>
              创建第一个订单
            </button>
          </div>
        ) : (
          <div className="table-wrapper" onScroll={handleTableScroll}>
            <table className="orders-table">
              <thead>
                <tr>
                  <th width="30"></th>
                  <th>日期</th>
                  <th>Company</th>
                  <th>发货地</th>
                  <th>收货地</th>
                  <th className="text-right">总重(lbs)</th>
                  <th className="text-right">总体积(ft³)</th>
                  <th className="text-right">总件数</th>
                  <th className="text-right">WE报价</th>
                  <th className="text-right">司机参考价</th>
                  <th className="text-right">运输距离</th>
                  {currentStatus === 'ordered' && <th>状态</th>}
                  {currentStatus === 'ordered' && <th>卡车信息</th>}
                  <th className="meta-col">发货单号</th>
                  <th className="meta-col">WE单号</th>
                  <th className="meta-col">货物备注</th>
                  <th>操作员工</th>
                  <th>操作</th>
                  {(currentStatus === 'quote' || currentStatus === 'ordered') && (
                    <th>文档</th>
                  )}
                  {currentStatus === 'completed' && (
                    <>
                      <th>付款</th>
                      <th>文档</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <React.Fragment key={order.id}>
                    {/* 主行 */}
                    <tr 
                      className={`order-row ${order.sub_status ? `sub-status-${order.sub_status}` : ''} ${!canModifyOrder(order) ? 'order-locked' : ''}`}
                      data-order-id={order.id}
                      onClick={() => toggleRow(order.id)}
                    >
                      <td>
                        <button className="expand-btn">
                          {expandedRow === order.id ? '▼' : '▶'}
                        </button>
                      </td>
                      <td>
                        <EditableCell
                          value={order.quote_date}
                          orderId={order.id}
                          field="quote_date"
                          type="date"
                          onSave={handleCellUpdate}
                          formatDisplay={(v) => {
                            if (!v) return '-';
                            // 使用UTC时间避免时区转换问题
                            const date = new Date(v);
                            return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
                          }}
                        />
                      </td>
                      <td className="td-company">
                        <CompanyEditableCell
                          value={order.inquiry_company || order.customer_name}
                          orderId={order.id}
                          tentative={order.inquiry_company_confirmed === false && !!(order.inquiry_company || order.customer_name)}
                          onSave={handleCompanySave}
                        />
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <EditableCell
                          value={order.origin_address}
                          orderId={order.id}
                          field="origin_address"
                          type="text"
                          onSave={async (id, field, newValue) => {
                            await handleCellUpdate(id, field, newValue);
                            const updatedOrder = orders.find(o => o.id === id);
                            const destAddr = updatedOrder?.destination_address || order.destination_address;
                            if (newValue && destAddr) {
                              setTimeout(() => {
                                calculateAddressDetails(id, 'origin', newValue, destAddr);
                              }, 500);
                            }
                          }}
                          formatDisplay={() => {
                            const cityState = [order.origin_city, order.origin_state].filter(Boolean).join(', ');
                            const zip = order.origin_zipcode ? ` ${order.origin_zipcode}` : '';
                            return (cityState + zip) || '-';
                          }}
                        />
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <EditableCell
                          value={order.destination_address}
                          orderId={order.id}
                          field="destination_address"
                          type="text"
                          onSave={async (id, field, newValue) => {
                            await handleCellUpdate(id, field, newValue);
                            const updatedOrder = orders.find(o => o.id === id);
                            const originAddr = updatedOrder?.origin_address || order.origin_address;
                            if (originAddr && newValue) {
                              setTimeout(() => {
                                calculateAddressDetails(id, 'destination', originAddr, newValue);
                              }, 500);
                            }
                          }}
                          formatDisplay={() => {
                            const cityState = [order.destination_city, order.destination_state].filter(Boolean).join(', ');
                            const zip = order.destination_zipcode ? ` ${order.destination_zipcode}` : '';
                            return (cityState + zip) || '-';
                          }}
                        />
                      </td>
                      <td className="text-right">{formatNumber(order.total_weight_lbs)}</td>
                      <td className="text-right">
                        <EditableCell
                          value={order.total_volume}
                          orderId={order.id}
                          field="total_volume"
                          type="number"
                          onSave={handleCellUpdate}
                          formatDisplay={(v) => v ? `${parseFloat(v).toFixed(2)}` : '-'}
                        />
                      </td>
                      <td className="text-right">
                        <EditableCell
                          value={order.actual_pallets}
                          orderId={order.id}
                          field="actual_pallets"
                          type="number"
                          onSave={handleCellUpdate}
                          formatDisplay={(v) => v || '-'}
                        />
                      </td>
                      <td className="text-right price">
                        <EditableCell
                          value={order.ew_quote_price}
                          orderId={order.id}
                          field="ew_quote_price"
                          type="number"
                          onSave={handleCellUpdate}
                          formatDisplay={(v) => formatCurrency(v)}
                        />
                      </td>
                      <td className="text-right price">
                        <EditableCell
                          value={order.driver_reference_price}
                          orderId={order.id}
                          field="driver_reference_price"
                          type="number"
                          onSave={handleCellUpdate}
                          formatDisplay={(v) => formatCurrency(v)}
                        />
                      </td>
                      <td className="text-right">
                        <EditableCell
                          value={order.transport_distance}
                          orderId={order.id}
                          field="transport_distance"
                          type="number"
                          onSave={handleCellUpdate}
                          formatDisplay={(v) => v ? `${formatNumber(v)} mi` : '-'}
                        />
                      </td>
                      {currentStatus === 'ordered' && (
                        <td>{getSubStatusBadge(order.sub_status)}</td>
                      )}
                      {currentStatus === 'ordered' && (
                        <td>
                          {order.truck_company_name ? (
                            <div className="truck-info-mini">
                              {order.truck_company_name}
                              {order.mc_number && <div className="mc-num">MC# {order.mc_number}</div>}
                            </div>
                          ) : '-'}
                        </td>
                      )}
                      <td className="meta-col">
                        <EditableCell
                          value={order.shipment_number}
                          orderId={order.id}
                          field="shipment_number"
                          type="text"
                          onSave={handleCellUpdate}
                        />
                      </td>
                      <td className="meta-col order-number we-number">
                        <span className="we-number-display">
                          {order.order_number || '-'}
                        </span>
                      </td>
                      <td className="meta-col">
                        <EditableCell
                          value={order.cargo_type}
                          orderId={order.id}
                          field="cargo_type"
                          type="text"
                          onSave={handleCellUpdate}
                        />
                      </td>
                      <td>
                        {order.status === 'quote' ? (
                          /* 报价单阶段：显示创建/操作人 */
                          (order.creator_info?.name || '-')
                        ) : isAdmin ? (
                          /* 已下单及之后：Admin 可在此重新分配 Sales */
                          <select
                            className="reassign-select"
                            value={order.assigned_to || ''}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); handleReassign(order.id, e.target.value); }}
                            title="重新分配 Sales（仅管理员）"
                          >
                            <option value="">未分配</option>
                            {salesList.map((s) => (
                              <option key={s.id} value={String(s.id)}>{s.name}</option>
                            ))}
                          </select>
                        ) : (
                          /* 非 Admin：仅显示被分配的 Sales，不可更改 */
                          (order.assignee_info?.name || order.confirmer_info?.name || '-')
                        )}
                      </td>
                      <td>
                        {!canModifyOrder(order) ? (
                          <span className="locked-indicator" title="该订单已分配给其他 Sales，您没有修改权限">🔒 只读</span>
                        ) : order.status === 'quote' ? (
                          <div className="quote-actions">
                            <button
                              className="btn-quote-icon btn-quote-confirm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConfirmOrder(order.id);
                              }}
                              title="确认下单"
                              aria-label="确认下单"
                            >
                              <Send size={13} strokeWidth={2.2} />
                              <span>下单</span>
                            </button>
                            <button
                              className="btn-quote-icon btn-quote-map"
                              onClick={(e) => {
                                e.stopPropagation();
                                const origin = order.origin_address || `${order.origin_city}, ${order.origin_state} ${order.origin_zipcode}`;
                                const dest = order.destination_address || `${order.destination_city}, ${order.destination_state} ${order.destination_zipcode}`;
                                window.open(`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}&travelmode=driving`, '_blank');
                              }}
                              title="在 Google Maps 查看路线"
                              aria-label="路线"
                            >
                              <MapIcon size={13} strokeWidth={2.2} />
                            </button>
                            {user?.employeeRole === 'admin' && (
                              <button
                                className="btn-quote-icon btn-quote-delete"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`确定要删除 ${order.order_number} 吗？`)) return;
                                  try {
                                    await orderApi.deleteOrder(order.id);
                                    loadOrders();
                                  } catch (err) {
                                    alert('删除失败: ' + err.message);
                                  }
                                }}
                                title="删除订单"
                                aria-label="删除"
                              >
                                <Trash2 size={13} strokeWidth={2.2} />
                              </button>
                            )}
                          </div>
                        ) : order.status === 'ordered' ? (
                          <div className="ordered-actions">
                            <div 
                              className="sub-status-dropdown"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <select
                                value={order.sub_status || 'waiting_driver'}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newStatus = e.target.value;
                                  if (newStatus === 'completed') {
                                    handleCompleteOrder(order.id);
                                  } else if (newStatus === 'claim') {
                                    handleRequestClaim(order.id);
                                  } else if (newStatus === 'cancel') {
                                    handleCancelOrder(order.id);
                                  } else if (newStatus === 'back_to_quote') {
                                    handleBackToQuote(order.id);
                                  } else {
                                    handleUpdateSubStatus(order.id, newStatus);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="sub-status-select"
                              >
                                <option value="waiting_driver">寻找司机</option>
                                <option value="driver_found">找到司机</option>
                                <option value="sent_to_3pl">给3PL</option>
                                <option value="in_transit">运输中</option>
                                <option value="completed">已完成</option>
                                <option value="claim">需要索赔</option>
                                <option value="back_to_quote">回到报价</option>
                                <option value="cancel">取消订单</option>
                              </select>
                            </div>
                          </div>
                        ) : order.status === 'completed' ? (
                          <div className="completed-actions">
                            <div 
                              className="sub-status-dropdown"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <select
                                value="completed"
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newStatus = e.target.value;
                                  if (newStatus === 'reopen') {
                                    handleReopenOrder(order.id);
                                  } else if (newStatus === 'claim') {
                                    handleRequestClaim(order.id);
                                  } else if (newStatus === 'cancel') {
                                    handleCancelOrder(order.id);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="sub-status-select"
                              >
                                <option value="completed">已完成</option>
                                <option value="reopen">重新打开</option>
                                <option value="claim">需要索赔</option>
                                <option value="cancel">取消订单</option>
                              </select>
                            </div>
                          </div>
                        ) : order.status === 'cancelled' ? (
                          <div className="cancelled-actions">
                            <div 
                              className="sub-status-dropdown"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <select
                                value="cancelled"
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newStatus = e.target.value;
                                  if (newStatus === 'reopen') {
                                    handleReopenOrder(order.id);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="sub-status-select"
                              >
                                <option value="cancelled">已取消</option>
                                <option value="reopen">重新打开</option>
                              </select>
                            </div>
                          </div>
                        ) : (
                          <span className="no-action">-</span>
                        )}
                      </td>
                      {(currentStatus === 'quote' || currentStatus === 'ordered') && renderDocsCell(order)}
                      {currentStatus === 'completed' && (
                        <>
                          {/* 付款状态 */}
                          <td onClick={(e) => e.stopPropagation()}>
                            <select
                              value={order.payment_status || 'unpaid'}
                              onChange={(e) => handleMarkPaid(order.id, e.target.value)}
                              className="payment-status-select"
                              style={{
                                fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid #e5e7eb', cursor: 'pointer',
                                color: order.payment_status === 'paid' ? '#16a34a' : order.payment_status === 'partial' ? '#f59e0b' : '#ef4444',
                                fontWeight: 600, background: 'white'
                              }}
                            >
                              <option value="unpaid">未付</option>
                              <option value="partial">部分</option>
                              <option value="paid">已付</option>
                            </select>
                          </td>
                          {renderDocsCell(order)}
                        </>
                      )}
                    </tr>

                    {/* 展开行（隐藏字段） */}
                    {expandedRow === order.id && (
                      <tr className={`expanded-row ${!canModifyOrder(order) ? 'order-locked' : ''}`}>
                        <td colSpan="100%">
                          <div className="expanded-content">

                            {/* === 顶部区：详细地址 + 货物明细 两列并排，节省高度 === */}
                            <div className="ltl-top-grid">
                            {/* === 1. 详细地址 === */}
                            <div className="ltl-address-section">
                              <h4 className="ltl-section-title">详细地址（双击地址栏会自动算距离、城市、邮编）</h4>
                              <div className="address-row">
                                <div className="address-field">
                                  <label>发货地址</label>
                                  <EditableCell
                                    value={order.origin_address}
                                    orderId={order.id}
                                    field="origin_address"
                                    type="text"
                                    onSave={async (id, field, newValue) => {
                                      await handleCellUpdate(id, field, newValue);
                                      const updatedOrder = orders.find(o => o.id === id);
                                      const destAddr = updatedOrder?.destination_address || order.destination_address;
                                      if (newValue && destAddr) {
                                        setTimeout(() => {
                                          calculateAddressDetails(id, 'origin', newValue, destAddr);
                                        }, 500);
                                      }
                                    }}
                                  />
                                  <div className="address-meta">
                                    {[order.origin_city, order.origin_state].filter(Boolean).join(', ')}
                                    {order.origin_zipcode && ` ${order.origin_zipcode}`}
                                  </div>
                                </div>
                                <div className="address-field">
                                  <label>收货地址</label>
                                  <EditableCell
                                    value={order.destination_address}
                                    orderId={order.id}
                                    field="destination_address"
                                    type="text"
                                    onSave={async (id, field, newValue) => {
                                      await handleCellUpdate(id, field, newValue);
                                      const updatedOrder = orders.find(o => o.id === id);
                                      const originAddr = updatedOrder?.origin_address || order.origin_address;
                                      if (originAddr && newValue) {
                                        setTimeout(() => {
                                          calculateAddressDetails(id, 'destination', originAddr, newValue);
                                        }, 500);
                                      }
                                    }}
                                  />
                                  <div className="address-meta">
                                    {[order.destination_city, order.destination_state].filter(Boolean).join(', ')}
                                    {order.destination_zipcode && ` ${order.destination_zipcode}`}
                                  </div>
                                </div>
                                <div className="address-extras">
                                  <div className="address-field-small">
                                    <label>距离 (mi)</label>
                                    <EditableCell
                                      value={order.transport_distance}
                                      orderId={order.id}
                                      field="transport_distance"
                                      type="number"
                                      onSave={handleCellUpdate}
                                      formatDisplay={(v) => v ? `${formatNumber(v)} mi` : '-'}
                                    />
                                  </div>
                                  <div className="address-field-small">
                                    <label>地址类型</label>
                                    <EditableCell
                                      value={order.address_type}
                                      orderId={order.id}
                                      field="address_type"
                                      type="select"
                                      options={[
                                        { value: 'Residential', label: 'Residential' },
                                        { value: 'Commercial+Lift', label: 'Commercial+Lift' },
                                        { value: 'Commercial', label: 'Commercial' },
                                        { value: 'Warehouse', label: 'Warehouse' }
                                      ]}
                                      onSave={async (id, field, newValue) => {
                                        const truckType = newValue === 'Residential' ? '13' : '26';
                                        await handleCellUpdate(id, field, newValue);
                                        await handleCellUpdate(id, 'truck_pallets', truckType);
                                        const currentOrder = orders.find(o => o.id === id);
                                        if (currentOrder) {
                                          const updatedOrder = { ...currentOrder, address_type: newValue, truck_pallets: truckType };
                                          const calculations = calculateQuoteReferences(updatedOrder);
                                          await orderApi.updateOrder(id, calculations);
                                          setOrders(prevOrders =>
                                            prevOrders.map(o => o.id === id ? { ...o, ...updatedOrder, ...calculations } : o)
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* 收货人信息（AI 解析时自动填入） */}
                              <div className="recipient-row">
                                <div className="address-field-small recipient-field">
                                  <label>收货人</label>
                                  <EditableCell
                                    value={order.recipient_name}
                                    orderId={order.id}
                                    field="recipient_name"
                                    type="text"
                                    onSave={handleCellUpdate}
                                    formatDisplay={(v) => v || '-'}
                                  />
                                </div>
                                <div className="address-field-small recipient-field">
                                  <label>收货人电话</label>
                                  <EditableCell
                                    value={order.recipient_phone}
                                    orderId={order.id}
                                    field="recipient_phone"
                                    type="text"
                                    onSave={handleCellUpdate}
                                    formatDisplay={(v) => v || '-'}
                                  />
                                </div>
                                <div className="address-field-small recipient-field">
                                  <label>收货人邮箱</label>
                                  <EditableCell
                                    value={order.recipient_email}
                                    orderId={order.id}
                                    field="recipient_email"
                                    type="text"
                                    onSave={handleCellUpdate}
                                    formatDisplay={(v) => v || '-'}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* === 2. 货物明细 === */}
                            <CargoItemsList
                              orderId={order.id}
                              weightList={order.weight_list}
                              dimensionsList={order.dimensions_list}
                              totalWeightLbs={order.total_weight_lbs}
                              totalVolume={order.total_volume}
                              actualPallets={order.actual_pallets}
                              onSave={async (orderId, updates) => {
                                await orderApi.updateOrder(orderId, updates);
                                setOrders(prevOrders =>
                                  prevOrders.map(o =>
                                    o.id === orderId ? { ...o, ...updates } : o
                                  )
                                );
                                loadOrders();
                              }}
                              readOnly={!canModifyOrder(order) || currentStatus === 'completed' || currentStatus === 'cancelled'}
                            />
                            </div>
                            {/* /ltl-top-grid */}

                            {/* Workflow Stage Indicator */}
                            {order.workflow_stage && (
                              <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fafbfc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                  <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Workflow Stage</h4>
                                  <select
                                    value={order.workflow_stage}
                                    onChange={async (e) => {
                                      try {
                                        await orderApi.advanceWorkflowStage(order.id, e.target.value);
                                        loadOrders();
                                      } catch (err) { alert('Stage update failed: ' + err.message); }
                                    }}
                                    style={{ fontSize: '0.8rem', padding: '3px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}
                                  >
                                    {['inquiry','quoting','quote_confirmed','pre_bol','bol_issued','carrier_sourcing','pickup','in_transit','delivered','invoicing','settlement','completed','cancelled'].map(s => (
                                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ display: 'flex', gap: 2 }}>
                                  {['inquiry','quoting','quote_confirmed','pre_bol','bol_issued','carrier_sourcing','pickup','in_transit','delivered','invoicing','settlement','completed'].map((stage, idx) => {
                                    const stageOrder = ['inquiry','quoting','quote_confirmed','pre_bol','bol_issued','carrier_sourcing','pickup','in_transit','delivered','invoicing','settlement','completed'];
                                    const currentIdx = stageOrder.indexOf(order.workflow_stage);
                                    const isActive = idx <= currentIdx;
                                    const isCurrent = stage === order.workflow_stage;
                                    return (
                                      <div key={stage} style={{
                                        flex: 1, height: 6, borderRadius: 3,
                                        background: order.workflow_stage === 'cancelled' ? '#fca5a5' : isActive ? '#22c55e' : '#e5e7eb',
                                        border: isCurrent ? '1px solid #16a34a' : 'none',
                                      }} title={stage.replace(/_/g, ' ')} />
                                    );
                                  })}
                                </div>
                                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: '0.78rem', color: '#6b7280', flexWrap: 'wrap' }}>
                                  {order.bol_number && <span>Customer PO#: <strong>{order.bol_number}</strong></span>}
                                  {order.sourcing_channel_id && sourcingChannels.length > 0 && (
                                    <span>Channel: <strong>{sourcingChannels.find(c => c.id === order.sourcing_channel_id)?.name || '—'}</strong></span>
                                  )}
                                  {order.delivered_at && <span>Delivered: {new Date(order.delivered_at).toLocaleDateString('zh-CN')}</span>}
                                  {order.invoiced_at && <span>Invoiced: {new Date(order.invoiced_at).toLocaleDateString('zh-CN')}</span>}
                                  {order.settled_at && <span>Settled: {new Date(order.settled_at).toLocaleDateString('zh-CN')}</span>}
                                  {order.cancelled_at && <span style={{ color: '#ef4444' }}>Cancelled: {new Date(order.cancelled_at).toLocaleDateString('zh-CN')}{order.cancel_reason ? ` (${order.cancel_reason})` : ''}</span>}
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                                  <div style={{ flex: 1, minWidth: 100 }}>
                                    <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Customer PO#</label>
                                    <EditableCell value={order.bol_number} orderId={order.id} field="bol_number" type="text" onSave={handleCellUpdate} />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 100 }}>
                                    <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Sourcing Channel</label>
                                    <EditableCell
                                      value={order.sourcing_channel_id}
                                      orderId={order.id}
                                      field="sourcing_channel_id"
                                      type="select"
                                      options={[{ value: '', label: '—' }, ...sourcingChannels.map(c => ({ value: String(c.id), label: c.name }))]}
                                      onSave={handleCellUpdate}
                                    />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 150 }}>
                                    <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Consignee Contact</label>
                                    <EditableCell value={order.consignee_contact} orderId={order.id} field="consignee_contact" type="text" onSave={handleCellUpdate} />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 120 }}>
                                    <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Delivery Type</label>
                                    <EditableCell
                                      value={order.delivery_type_detail}
                                      orderId={order.id}
                                      field="delivery_type_detail"
                                      type="select"
                                      options={[{ value: '', label: '—' }, { value: 'Warehouse', label: 'Warehouse' }, { value: 'Residential', label: 'Residential' }, { value: 'Commercial', label: 'Commercial' }, { value: 'Commercial+Lift', label: 'Commercial+Lift' }]}
                                      onSave={handleCellUpdate}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Order Loads Panel */}
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Loads (Individual Cargo)</h4>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  {!orderLoads[order.id] && (
                                    <button
                                      style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}
                                      onClick={() => loadOrderLoads(order.id)}
                                      disabled={loadsLoading[order.id]}
                                    >
                                      {loadsLoading[order.id] ? 'Loading...' : 'Load Details'}
                                    </button>
                                  )}
                                  {orderLoads[order.id] && orderLoads[order.id].length === 0 && (
                                    <button
                                      style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: 4, border: '1px solid #d1d5db', background: '#f0f7ff', cursor: 'pointer' }}
                                      onClick={async () => {
                                        try {
                                          const dims = order.dimensions_list ? JSON.parse(order.dimensions_list) : [];
                                          const wts = order.weight_list ? JSON.parse(order.weight_list) : [];
                                          const items = dims.map((d, i) => ({
                                            description: order.cargo_description_detailed || '',
                                            weight: wts[i] || 0,
                                            length: d.length || 0,
                                            width: d.width || 0,
                                            height: d.height || 0,
                                            pallets: d.pieces || 1,
                                            freightClass: '',
                                          }));
                                          if (items.length === 0 && order.total_weight_lbs) {
                                            items.push({ description: order.cargo_description_detailed || 'Cargo', weight: order.total_weight_lbs, pallets: order.actual_pallets || 1 });
                                          }
                                          if (items.length > 0) {
                                            await orderLoadApi.bulkCreate(order.id, items);
                                            loadOrderLoads(order.id);
                                          }
                                        } catch (e) { alert('Failed to create loads: ' + e.message); }
                                      }}
                                    >
                                      Auto-Create from Dimensions
                                    </button>
                                  )}
                                </div>
                              </div>
                              {orderLoads[order.id] && orderLoads[order.id].length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                  <thead>
                                    <tr style={{ background: '#f1f5f9' }}>
                                      <th style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>#</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Pallets</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Weight</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>L x W x H</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Class</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Quote</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Driver $</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {orderLoads[order.id].map((ld, idx) => (
                                      <tr key={ld.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '5px 8px' }}>{ld.load_number || idx + 1}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>{ld.pallet_count || '—'}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 500 }}>{ld.weight_lbs ? `${Number(ld.weight_lbs).toLocaleString()} lbs` : '—'}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>{ld.length_in && ld.width_in && ld.height_in ? `${ld.length_in}x${ld.width_in}x${ld.height_in}` : '—'}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>{ld.freight_class || '—'}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                          <span style={{
                                            padding: '1px 6px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 500,
                                            background: ld.status === 'delivered' ? '#dcfce7' : ld.status === 'in_transit' ? '#dbeafe' : ld.status === 'damaged' || ld.status === 'lost' ? '#fee2e2' : '#f3f4f6',
                                            color: ld.status === 'delivered' ? '#16a34a' : ld.status === 'in_transit' ? '#2563eb' : ld.status === 'damaged' || ld.status === 'lost' ? '#ef4444' : '#6b7280',
                                          }}>{ld.status}</span>
                                        </td>
                                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>{ld.customer_quote ? `$${Number(ld.customer_quote).toFixed(0)}` : '—'}</td>
                                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>{ld.driver_price ? `$${Number(ld.driver_price).toFixed(0)}` : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>

                            {/* === 报价分析 (Pricing Analysis) - 分组显示 === */}
                            <div className="ltl-pricing-card">
                              <h4 className="ltl-card-title">报价分析 / Pricing Analysis</h4>

                              <div className="pricing-top-row">
                              <div className="pricing-group">
                                <div className="group-label">输入参数</div>
                                <div className="group-fields">
                                  <div className="pricing-field">
                                    <label>总面积板数</label>
                                    <EditableCell
                                      value={order.total_area_pallets}
                                      orderId={order.id}
                                      field="total_area_pallets"
                                      type="number"
                                      onSave={handleCellUpdateWithCalculation}
                                      formatDisplay={(v) => formatNumber(v)}
                                    />
                                  </div>
                                  <div className="pricing-field">
                                    <label>价格系数</label>
                                    <EditableCell
                                      value={order.truck_pallets}
                                      orderId={order.id}
                                      field="truck_pallets"
                                      type="select"
                                      options={[
                                        { value: '13', label: '13' },
                                        { value: '18', label: '18' },
                                        { value: '26', label: '26' }
                                      ]}
                                      onSave={handleCellUpdateWithCalculation}
                                      formatDisplay={(v) => v || '-'}
                                    />
                                  </div>
                                  <div className="pricing-field">
                                    <label>TOTAL DAT</label>
                                    <EditableCell
                                      value={order.total_dat}
                                      orderId={order.id}
                                      field="total_dat"
                                      type="number"
                                      onSave={handleCellUpdateWithCalculation}
                                      formatDisplay={(v) => formatCurrency(v)}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="pricing-group">
                                <div className="group-label">报价参考</div>
                                <div className="group-fields">
                                  <div className="pricing-field highlight">
                                    <label>参考</label>
                                    <span className="readonly-value strong">{formatCurrency(order.quote_reference)}</span>
                                  </div>
                                  <div className="pricing-field">
                                    <label>+10%</label>
                                    <span className="readonly-value">{formatCurrency(order.quote_ref_10)}</span>
                                  </div>
                                  <div className="pricing-field">
                                    <label>+20%</label>
                                    <span className="readonly-value">{formatCurrency(order.quote_ref_20)}</span>
                                  </div>
                                  <div className="pricing-field">
                                    <label>+30%</label>
                                    <span className="readonly-value">{formatCurrency(order.quote_ref_30)}</span>
                                  </div>
                                </div>
                              </div>
                              </div>
                              {/* /pricing-top-row */}

                              {/* 平台比价：Priority1 为母集，Quote# / Quote Company 为其子集 */}
                              <div className="pricing-group pricing-platform">
                                <div className="group-label">平台比价</div>
                                <div className="group-fields">
                                  <div className="priority-subgroup">
                                    <div className="priority-subgroup-title">Priority1</div>
                                    <div className="priority-subgroup-fields">
                                      <div className="pricing-field">
                                        <label>Quote#</label>
                                        <EditableCell
                                          value={order.quote_no}
                                          orderId={order.id}
                                          field="quote_no"
                                          type="text"
                                          onSave={handleCellUpdate}
                                          formatDisplay={(v) => v || '-'}
                                        />
                                      </div>
                                      <div className="pricing-field">
                                        <label>Quote Company1</label>
                                        <EditableCell
                                          value={order.quote_company_1}
                                          orderId={order.id}
                                          field="quote_company_1"
                                          type="text"
                                          onSave={handleCellUpdate}
                                          formatDisplay={(v) => v || '-'}
                                        />
                                      </div>
                                      <div className="pricing-field">
                                        <label>Company1 Price</label>
                                        <EditableCell
                                          value={order.quote_company_1_price}
                                          orderId={order.id}
                                          field="quote_company_1_price"
                                          type="number"
                                          onSave={handleCellUpdate}
                                          formatDisplay={(v) => formatCurrency(v)}
                                        />
                                      </div>
                                      <div className="pricing-field">
                                        <label>Quote Company2</label>
                                        <EditableCell
                                          value={order.quote_company_2}
                                          orderId={order.id}
                                          field="quote_company_2"
                                          type="text"
                                          onSave={handleCellUpdate}
                                          formatDisplay={(v) => v || '-'}
                                        />
                                      </div>
                                      <div className="pricing-field">
                                        <label>Company2 Price</label>
                                        <EditableCell
                                          value={order.quote_company_2_price}
                                          orderId={order.id}
                                          field="quote_company_2_price"
                                          type="number"
                                          onSave={handleCellUpdate}
                                          formatDisplay={(v) => formatCurrency(v)}
                                        />
                                      </div>
                                      <div className="pricing-field">
                                        <label>Quote Company3</label>
                                        <EditableCell
                                          value={order.quote_company_3}
                                          orderId={order.id}
                                          field="quote_company_3"
                                          type="text"
                                          onSave={handleCellUpdate}
                                          formatDisplay={(v) => v || '-'}
                                        />
                                      </div>
                                      <div className="pricing-field">
                                        <label>Company3 Price</label>
                                        <EditableCell
                                          value={order.quote_company_3_price}
                                          orderId={order.id}
                                          field="quote_company_3_price"
                                          type="number"
                                          onSave={handleCellUpdate}
                                          formatDisplay={(v) => formatCurrency(v)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="pricing-field">
                                    <label>其他 API</label>
                                    <span className="readonly-value">{formatCurrency(order.other_api_price)}</span>
                                  </div>
                                  <div className="pricing-field">
                                    <label>理想报价</label>
                                    <EditableCell
                                      value={order.ideal_quote}
                                      orderId={order.id}
                                      field="ideal_quote"
                                      type="number"
                                      onSave={handleCellUpdate}
                                      formatDisplay={(v) => formatCurrency(v)}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="pricing-group pricing-actual">
                                <div className="group-label">实际报价 & 利润</div>
                                <div className="group-fields">
                                  <div className="pricing-field">
                                    <label>WE 报价</label>
                                    <EditableCell
                                      value={order.ew_quote_price}
                                      orderId={order.id}
                                      field="ew_quote_price"
                                      type="number"
                                      onSave={handleCellUpdate}
                                      formatDisplay={(v) => formatCurrency(v)}
                                    />
                                  </div>
                                  <div className="pricing-field">
                                    <label>客户 Extra</label>
                                    <EditableCell
                                      value={order.customer_extra_fee}
                                      orderId={order.id}
                                      field="customer_extra_fee"
                                      type="number"
                                      onSave={handleCellUpdate}
                                      formatDisplay={(v) => formatCurrency(v)}
                                    />
                                  </div>
                                  <div className="pricing-field">
                                    <label>司机参考价</label>
                                    <EditableCell
                                      value={order.driver_reference_price}
                                      orderId={order.id}
                                      field="driver_reference_price"
                                      type="number"
                                      onSave={handleCellUpdate}
                                      formatDisplay={(v) => formatCurrency(v)}
                                    />
                                  </div>
                                  <div className="pricing-field">
                                    <label>司机 Extra</label>
                                    <EditableCell
                                      value={order.driver_extra_fee}
                                      orderId={order.id}
                                      field="driver_extra_fee"
                                      type="number"
                                      onSave={handleCellUpdate}
                                      formatDisplay={(v) => formatCurrency(v)}
                                    />
                                  </div>
                                  <div className="pricing-field profit-field">
                                    <label>利润</label>
                                    <EditableCell
                                      value={order.profit}
                                      orderId={order.id}
                                      field="profit"
                                      type="number"
                                      onSave={handleCellUpdate}
                                      formatDisplay={(v) => <span className="profit-value">{formatCurrency(v)}</span>}
                                    />
                                  </div>
                                  <div className="pricing-field">
                                    <label>总货值</label>
                                    <EditableCell
                                      value={order.cargo_value}
                                      orderId={order.id}
                                      field="cargo_value"
                                      type="number"
                                      onSave={handleCellUpdate}
                                      formatDisplay={(v) => formatCurrency(v)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* === 承运商 & 司机 === (仅下单后) */}
                            {currentStatus !== 'quote' && (
                              <div className="ltl-carrier-card">
                                <h4 className="ltl-card-title">承运商 & 司机 / Carrier & Driver</h4>

                                <div className="carrier-grid">
                                  <div className="carrier-field">
                                    <label>卡车公司</label>
                                    <EditableCell value={order.truck_company_name} orderId={order.id} field="truck_company_name" type="text" onSave={handleCellUpdate} />
                                  </div>
                                  <div className="carrier-field">
                                    <label>MC #</label>
                                    <EditableCell value={order.mc_number} orderId={order.id} field="mc_number" type="text" onSave={handleCellUpdate} />
                                  </div>
                                  <div className="carrier-field">
                                    <label>DOT #</label>
                                    <EditableCell value={order.dot_number} orderId={order.id} field="dot_number" type="text" onSave={handleCellUpdate} />
                                  </div>
                                  <div className="carrier-field">
                                    <label>付卡车价格</label>
                                    <EditableCell value={order.truck_payment} orderId={order.id} field="truck_payment" type="number" onSave={handleCellUpdate} formatDisplay={(v) => formatCurrency(v)} />
                                  </div>
                                  <div className="carrier-field">
                                    <label>公司联络</label>
                                    <EditableCell value={order.truck_contact} orderId={order.id} field="truck_contact" type="text" onSave={handleCellUpdate} />
                                  </div>
                                  <div className="carrier-field">
                                    <label>Email</label>
                                    <EditableCell value={order.carrier_email} orderId={order.id} field="carrier_email" type="text" onSave={handleCellUpdate} />
                                  </div>
                                  <div className="carrier-field full">
                                    <label>Carrier Address</label>
                                    <EditableCell value={order.carrier_address} orderId={order.id} field="carrier_address" type="text" onSave={handleCellUpdate} />
                                  </div>
                                </div>

                                <div className="driver-grid">
                                  <div className="driver-primary">
                                    <div className="carrier-field">
                                      <label>主司机 — 姓名</label>
                                      <EditableCell value={order.driver_name} orderId={order.id} field="driver_name" type="text" onSave={handleCellUpdate} />
                                    </div>
                                    <div className="carrier-field">
                                      <label>主司机 — 电话</label>
                                      <EditableCell value={order.driver_phone} orderId={order.id} field="driver_phone" type="text" onSave={handleCellUpdate} />
                                    </div>
                                  </div>
                                </div>

                                {/* 备用司机 - 折叠 */}
                                <details className="backup-drivers-collapse">
                                  <summary>
                                    备用司机 ({[
                                      order.backup_driver_1_name,
                                      order.backup_driver_2_name,
                                      order.backup_driver_3_name,
                                    ].filter(Boolean).length || 0} 个已填)
                                  </summary>
                                  <div className="backup-drivers-grid">
                                    {[1, 2, 3].map((n) => (
                                      <div key={n} className="backup-driver-mini">
                                        <div className="backup-driver-label">备用 #{n}</div>
                                        <div className="carrier-field">
                                          <label>姓名</label>
                                          <EditableCell value={order[`backup_driver_${n}_name`]} orderId={order.id} field={`backup_driver_${n}_name`} type="text" onSave={handleCellUpdate} />
                                        </div>
                                        <div className="carrier-field">
                                          <label>电话</label>
                                          <EditableCell value={order[`backup_driver_${n}_phone`]} orderId={order.id} field={`backup_driver_${n}_phone`} type="text" onSave={handleCellUpdate} />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              </div>
                            )}

                            {/* Carrier Quotes from custom_fields — 放在最后 */}
                            {(() => {
                              try {
                                const cf = typeof order.custom_fields === 'string' ? JSON.parse(order.custom_fields) : order.custom_fields;
                                const cq = cf?.carrier_quotes;
                                if (!cq || cq.length === 0) return null;
                                return (
                                  <div style={{ marginBottom: 0, padding: '10px 14px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
                                    <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Carrier Quotes ({cq.length})</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                      <thead>
                                        <tr style={{ background: '#fef3c7' }}>
                                          <th style={{ padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid #fde68a' }}>Carrier</th>
                                          <th style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #fde68a' }}>Price</th>
                                          <th style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #fde68a' }}>Transit</th>
                                          <th style={{ padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid #fde68a' }}>Service</th>
                                          <th style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #fde68a' }}>Guaranteed</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {cq.sort((a, b) => (a.price || Infinity) - (b.price || Infinity)).map((q, idx) => (
                                          <tr key={idx} style={{ borderBottom: '1px solid #fef3c7', background: idx === 0 ? '#f0fdf4' : 'transparent' }}>
                                            <td style={{ padding: '4px 8px', fontWeight: idx === 0 ? 600 : 400 }}>{q.carrier || '—'}</td>
                                            <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600, color: idx === 0 ? '#16a34a' : '#333' }}>${Number(q.price || 0).toFixed(2)}</td>
                                            <td style={{ padding: '4px 8px', textAlign: 'center' }}>{q.transitDays || '—'}</td>
                                            <td style={{ padding: '4px 8px', fontSize: '0.75rem' }}>{q.serviceType || '—'}</td>
                                            <td style={{ padding: '4px 8px', textAlign: 'center' }}>{q.isGuaranteed ? '✓' : '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                );
                              } catch { return null; }
                            })()}

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '14px 0', color: '#6b7280', fontSize: 12 }}>
                加载中...
              </div>
            )}
            {!hasMore && orders.length > 0 && (
              <div style={{ textAlign: 'center', padding: '14px 0', color: '#9ca3af', fontSize: 12 }}>
                — 已显示全部 {orders.length} 条 —
              </div>
            )}
          </div>
        )}
      </>
      )}
      </div>

      {/* 确认下单模态框 */}
      {showConfirmModal && selectedOrder && (
        <ConfirmOrderModal
          order={selectedOrder}
          salesList={salesList}
          defaultSalesId={selectedOrder?.assigned_to || selectedOrder?.created_by || ''}
          onClose={() => {
            setShowConfirmModal(false);
            setSelectedOrder(null);
          }}
          onConfirm={handleConfirmOrderSubmit}
        />
      )}

      {/* 文档生成器对话框 */}
      <DocumentGenerator
        isOpen={showDocGenerator}
        onClose={() => {
          setShowDocGenerator(false);
          setDocumentType(null);
        }}
        documentType={documentType}
        orders={orders}
      />

      {/* Invoice 生成器对话框 */}
      <InvoiceGenerator
        isOpen={showInvoiceGenerator}
        onClose={() => setShowInvoiceGenerator(false)}
        orders={orders}
      />

      {/* 报价生成器对话框 */}
      <QuoteGenerator
        isOpen={showQuoteGenerator}
        onClose={() => setShowQuoteGenerator(false)}
        orders={orders}
      />

      {/* 司机报价生成器对话框 */}
      <DriverQuoteGenerator
        isOpen={showDriverQuoteGenerator}
        onClose={() => setShowDriverQuoteGenerator(false)}
        orders={orders}
      />

      {/* QBO 设置对话框 */}
      <QBOSettings
        isOpen={showQBOSettings}
        onClose={() => setShowQBOSettings(false)}
      />
    </div>
  );
};

export default BrokerOrdersNew;

